import { Badge } from '@/components/common/badge'
import { Note } from '@/components/common/note'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/common/table'
import { TabsContent } from '@/components/common/tabs'
import { useConnectWalletModalContext } from '@/components/connect/connectModal'
import { AmountButtons } from '@/components/opportunity/sidePanel/amountButtons'
import { DepositLimitWarning } from '@/components/opportunity/sidePanel/depositLimitWarning'
import { TxAmountInput } from '@/components/opportunity/sidePanel/txAmountInput'
import { TxSteps } from '@/components/opportunity/sidePanel/txSteps'
import { useDepositLimit } from '@/components/opportunity/sidePanel/useDepositLimit'
import { useMaxAmount } from '@/components/opportunity/sidePanel/useMaxAmount'
import type { TabConfig } from '@/components/opportunity/useTabsConfig'
import { useTransaction } from '@/hooks/useTransaction'
import { useActionsQuery } from '@/queries/actions.query'
import type { TransactionsContext } from '@/queries/transactionsContext.query'
import type { StepData } from '@/types/transactionsContext'
import type { Vault } from '@/types/vault'
import { calculateTokenPriceInUsd } from '@/utils/calculateTokenPriceInUsd'
import { cn } from '@/utils/cn'
import { formatPreciseTokenValue, formatTokenValue, formatUSD } from '@vaultsfyi/common/utils'
import { useState } from 'react'
import type { Address } from 'viem'
import { formatUnits } from 'viem'
import { ActionNotes } from './actionNotes'

export type MellowSpecificData = {
  requestData: {
    claimableDeposit: string
    claimableRedeem: string
    pendingDeposit: string
    pendingRedeem: string
    redeemRequests: {
      timestamp: number
      lpTokenAmount: string
      isClaimable: boolean
    }[]
  }
}

interface MellowDepositTabProps {
  account: Address | undefined
  vault: Vault
  stepData: StepData
  tabConfig: TabConfig
  transactionsContext: TransactionsContext
  setTransactionFinished: ({ txHash, amount }: { txHash: string | undefined; amount: string }) => void
  txState: 'idle' | 'loading' | 'success'
}

export function MellowDepositTab({
  account,
  vault,
  stepData,
  tabConfig,
  transactionsContext,
  setTransactionFinished,
  txState,
}: MellowDepositTabProps) {
  const { setOpenModal: setOpenWalletModal } = useConnectWalletModalContext()
  const { network, asset, lpToken } = vault
  const { getParsedAmount, walletBalance, actionDescription, form } = tabConfig
  const inputAmount = form.watch('amount')
  const parsedAmount = getParsedAmount(inputAmount || '0')
  const [useAllFunds, setUseAllFunds] = useState(() => !form.getValues('amount'))

  const mellowData = transactionsContext?.vaultSpecificData as MellowSpecificData | undefined
  const requestData = mellowData?.requestData

  const pendingDeposit = BigInt(requestData?.pendingDeposit ?? 0)
  const claimableDeposit = BigInt(requestData?.claimableDeposit ?? 0)

  const isClaimable = claimableDeposit > 0n
  const isPending = pendingDeposit > 0n

  const currentAction = isClaimable ? 'claim-deposit' : 'request-deposit'
  const claimStep = transactionsContext?.allSteps.find((step) => step.name === 'claim-deposit')
  const activeStepData = isClaimable && claimStep ? claimStep : stepData

  const { data: actions, isFetching } = useActionsQuery({
    address: account,
    action: currentAction,
    amount: isClaimable ? '0' : parsedAmount,
    vault,
    all: useAllFunds,
    ignoreAmount: isClaimable,
  })

  const { executeTx, currentActionIndex } = useTransaction(network.name, actions, inputAmount, setTransactionFinished)

  const { depositLimit, depositLimitValue, isAboveDepositLimit } = useDepositLimit({
    transactionsContext,
    parsedAmount,
    assetDecimals: asset.decimals,
    isDepositAction: currentAction === 'request-deposit',
  })

  const onSubmit = form.handleSubmit(async () => {
    if (!account) {
      setOpenWalletModal('connect')
      return
    }
    await executeTx()
  })

  if (!requestData) {
    return (
      <TabsContent value={stepData.name} className="mt-3 flex flex-col gap-3 md:mt-4 md:gap-4">
        <p className="text-center text-xs">Loading...</p>
      </TabsContent>
    )
  }

  if (isPending && !isClaimable) {
    return (
      <TabsContent value={stepData.name} className="mt-3 flex flex-col gap-3 md:mt-4 md:gap-4">
        <Table>
          <TableHeader>
            <TableRow variant="header">
              <TableHead>Amount</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            <TableRow>
              <TableCell>
                {formatTokenValue(
                  { value: pendingDeposit.toString(), decimals: asset.decimals },
                  { minimumFractionDigits: 2 }
                )}{' '}
                {asset.symbol}
              </TableCell>
              <TableCell>
                <Badge color="orange">Pending</Badge>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
        <div className="flex flex-col gap-2 text-center">
          <p className="font-bold text-heading-foreground text-m">Deposit in progress...</p>
          <div className="text-gray-500 text-sm">
            Your deposit request is being processed. Once the next batch is executed, you will be able to claim your LP
            tokens.
          </div>
        </div>
      </TabsContent>
    )
  }

  const noInputAmount = parsedAmount === '0'
  const isSubmitDisabled = isClaimable ? false : noInputAmount || isAboveDepositLimit
  const isSubmitLoading = isFetching || txState === 'loading'

  const inputUsdValue = formatUSD(calculateTokenPriceInUsd(Number(inputAmount), Number(asset.assetPriceInUsd)) || '0')
  const balanceValue = formatPreciseTokenValue({
    value: walletBalance,
    decimals: asset.decimals,
  })
  const isNonIdle = txState !== 'idle'

  const actionLabel = isClaimable ? 'Claim Deposit' : 'Request Deposit'
  const { handleMaxClick } = useMaxAmount({ form, maxAmount: walletBalance, decimals: asset.decimals, setUseAllFunds })

  return (
    <form onSubmit={onSubmit}>
      <TabsContent value={stepData.name} className="mt-3 flex flex-col gap-3 md:mt-4 md:gap-4">
        {isClaimable && <p className={cn('text-xs', isNonIdle && 'hidden')}>Claim your deposit shares</p>}

        {!isClaimable && (
          <>
            <TxAmountInput
              amountForm={form}
              className={cn(isNonIdle && 'hidden')}
              actionDescription={actionDescription}
              usdValue={inputUsdValue}
              balance={balanceValue}
              asset={asset}
              onBalanceClick={handleMaxClick}
              onAmountChange={() => setUseAllFunds(false)}
            />
            <div className={cn(isNonIdle && 'hidden')}>
              <AmountButtons form={form} maxInputAmount={walletBalance} setUseAllFunds={setUseAllFunds} />
            </div>
          </>
        )}

        {isAboveDepositLimit && depositLimitValue && depositLimit !== undefined && (
          <DepositLimitWarning
            depositLimitValue={depositLimitValue}
            assetSymbol={asset.symbol}
            onClick={() => {
              form.setValue('amount', formatUnits(depositLimit, asset.decimals), { shouldValidate: true })
              setUseAllFunds(false)
            }}
          />
        )}

        {isClaimable && (
          <Note color="green">
            <p>
              Your deposit has been processed. You can now claim your{' '}
              <span className="font-bold">
                {formatTokenValue(
                  {
                    value: requestData.claimableDeposit,
                    decimals: asset.decimals,
                  },
                  { minimumFractionDigits: 2 }
                )}{' '}
                {lpToken?.symbol ?? 'LP'}
              </span>
            </p>
          </Note>
        )}

        <TxSteps
          account={account}
          stepData={activeStepData}
          activeStep={currentActionIndex}
          loading={isSubmitLoading}
          disabled={isSubmitDisabled}
          label={actionLabel}
          network={network.name}
          success={txState === 'success'}
        />

        <ActionNotes
          vault={vault}
          stepData={activeStepData}
          transactionsContext={transactionsContext}
          inputAmount={inputAmount}
        />
      </TabsContent>
    </form>
  )
}
